import { Action, ActionKind } from '@dynamic/ui';
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import styled from 'styled-components';

import { LinkStyleProps, MenuButton } from '#/components/Header/components';
import { useMoreMenuRoutes } from '#/components/Header/useMoreMenuRoutes';
import { useActions } from '#/hooks/actions/useActions';
import { useClickOutside } from '#/hooks/useClickOutside';
import { useNavigation } from '#/hooks/useNavigation';
import { useOnLocationChange } from '#/hooks/useOnLocationChange';
import { MEDIA_QUERIES } from '#/hooks/useResponsiveValue';
import { ButtonVariant } from '#/service/constants';
import { remify } from '#/service/utils';
import { useAuthStore } from '#/store/auth/auth.store';
import { tb } from '#/store/translation/translation.store';

import { focusShadow, hoverOpacity } from '../styles';
import { TOP_MENU_HEIGHT_PX } from './Menu.utils';
import { SettingsRouteParams, useSettingsMenus } from './useSettingsMenus';

export interface SettingsMenuLinkProps {
  children?: ReactNode;
  menuButtonProps: Omit<LinkStyleProps, 'theme'>;
}

export const SettingsMenuLink: FC<SettingsMenuLinkProps> = ({
  children,
  menuButtonProps,
}) => {
  const { go } = useNavigation('SettingsMenuLink');
  const { settingId } = useParams<SettingsRouteParams>();
  const { settingsToUse } = useSettingsMenus();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [openedByClick, setOpenedByClick] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { handleAction, actions } = useActions();
  const { isLoggedIn, isAuthEnabled } = useAuthStore();

  const { moreMenuRoutes } = useMoreMenuRoutes({ settingsToUse });

  const isHighlighted = useMemo(() => {
    return (
      !!settingId ||
      visible ||
      moreMenuRoutes.some((route) => pathname.startsWith(route))
    );
  }, [pathname, moreMenuRoutes, settingId, visible]);

  useClickOutside(containerRef, () => {
    if (visible) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setVisible(false);
      setOpenedByClick(false);
    }
  });

  useOnLocationChange(() => {
    if (visible) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setVisible(false);
      setOpenedByClick(false);
    }
  });

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setVisible(true);
  };

  const handleMouseLeave = () => {
    if (!openedByClick) {
      closeTimeoutRef.current = setTimeout(() => {
        setVisible(false);
        closeTimeoutRef.current = null;
      }, 500);
    }
  };

  const handleClick = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    // Close menu if it's already pinned open
    if (visible && openedByClick) {
      setVisible(false);
      setOpenedByClick(false);

      return;
    }

    // Open menu and pin it (either from closed state or from hover state)
    setVisible(true);
    setOpenedByClick(true);
  };

  const handleItemClick = (key: string, action?: Action) => {
    setVisible(false);
    setOpenedByClick(false);

    if (action) {
      handleAction(action);

      return;
    }
    go(`/settings/${key}`);
  };

  const handleAuthentication = () => {
    setVisible(false);
    setOpenedByClick(false);

    if (isLoggedIn) {
      actions[ActionKind.Logout]?.();
    } else {
      actions[ActionKind.Login]?.();
    }
  };

  return (
    <SettingsLinkContainer
      data-testid="settings-link-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={containerRef}
    >
      <MenuButton
        {...menuButtonProps}
        $buttonVariant={ButtonVariant.Ghost}
        aria-expanded={visible}
        aria-haspopup="true"
        aria-label={tb('settings.appSettings')}
        className={isHighlighted ? 'active' : ''}
        id="settings-menu-trigger"
        onClick={handleClick}
        type="button"
      >
        {children}
      </MenuButton>
      <AbsoluteMenuList
        $isVisible={visible}
        aria-labelledby="settings-menu-trigger"
        data-testid="settings-menu"
      >
        {settingsToUse?.map(({ key = '', title = '', action }) => (
          <AbsoluteMenuItem key={key}>
            <AbsoluteMenuButton
              data-testid="settings-menu-item"
              onClick={() => handleItemClick(key, action)}
              type="button"
            >
              {tb(title)}
            </AbsoluteMenuButton>
          </AbsoluteMenuItem>
        ))}
        {isAuthEnabled && (
          <AbsoluteMenuItem>
            <AbsoluteMenuButton
              data-testid="settings-menu-item"
              onClick={handleAuthentication}
              type="button"
            >
              {tb(isLoggedIn ? 'button.logout' : 'button.login')}
            </AbsoluteMenuButton>
          </AbsoluteMenuItem>
        )}
      </AbsoluteMenuList>
    </SettingsLinkContainer>
  );
};

SettingsMenuLink.displayName = 'SettingsMenuLink';

const SettingsLinkContainer = styled.span`
  height: 100%;
  display: flex;
`;

SettingsLinkContainer.displayName = 'SettingsLinkContainer';

interface AbsoluteMenuProps {
  $isVisible: boolean;
}

const AbsoluteMenuList = styled.ul<AbsoluteMenuProps>`
  display: block;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
  transform: ${({ $isVisible }) =>
    $isVisible ? 'translateY(0)' : 'translateY(-8px)'};
  transition:
    opacity 0.2s ease-in-out,
    visibility 0.2s ease-in-out,
    transform 0.2s ease-in-out;
  background-color: var(--pageSecondaryBackground);
  min-width: ${remify(200)};
  max-width: ${remify(400)};
  position: absolute;
  top: calc(${remify(TOP_MENU_HEIGHT_PX)} + var(--bannerHeight, 0rem));
  padding: 0.2rem 0.5rem;
  box-shadow: 0 ${remify(3)} ${remify(9)} var(--shadowColor);
  right: 2rem;
  margin: 0;

  @media ${MEDIA_QUERIES.lg} {
    right: 0.3rem;
  }
`;

AbsoluteMenuList.displayName = 'AbsoluteMenuList';

const AbsoluteMenuItem = styled.li`
  list-style: none;
`;

AbsoluteMenuItem.displayName = 'AbsoluteMenuItem';

const AbsoluteMenuButton = styled.button`
  opacity: 1;
  cursor: pointer;
  border: none;
  background: transparent;
  text-align: left;
  display: block;
  width: 100%;
  appearance: none;
  color: var(--pageParagraphForeground);
  font-size: 0.8889rem;
  line-height: 1.5;
  border-radius: 1px;

  /* override RightSide's padding */
  && {
    padding: ${remify(14)} ${remify(10)};
  }

  ${hoverOpacity}

  &:focus-visible {
    ${focusShadow}
  }
`;

AbsoluteMenuButton.displayName = 'AbsoluteMenuButton';
